(async () => {
    const { Core } = await importModule('Core');
    const { publishEvent } = await importModule('Utils');

    const STORAGE_KEY = 'unleashed:selected-vehicle';
    const DISABLE_FILTER_KEY = 'unleashed:fitment-filter-disabled';

    const normalizeBoltPattern = (s) => String(s).toUpperCase().replace(/\s+/g, '');

    // Custom-drilled (Blank/Custom bolt pattern) lead time varies by manufacturer. Anything not
    // listed here falls back to the original 3-4 weeks default.
    const BLANK_LEAD_TIME_BY_VENDOR = {
        'Stance': '1-2 weeks',
        'Vertini': '1-2 weeks',
        'Blaque Diamond': '1-2 weeks',
        'Avant Garde': '1-2 weeks',
        'AL13 Wheels': '3-4 days',
        'MV Forged': '3-4 days',
        'MRR': '3-4 days',
        'Variant': '3-4 weeks',
        'Vossen': '3-4 weeks',
    };
    const DEFAULT_BLANK_LEAD_TIME = '3-4 weeks';
    // Vendors where picking the "Blank (Custom)" bolt pattern swaps the fitment box text to the
    // custom-drilled explanation (instead of "matching bolt patterns below are highlighted").
    const BLANK_SELECTED_VENDORS = ['Vossen'];
    // Blank/custom-drilled wheels (Stance, Variant, Vertini, etc.) get their bolt pattern
    // machined to order after purchase, so they fit any vehicle regardless of what bolt
    // pattern the customer's vehicle actually needs.
    const isBlankBoltPattern = (s) => normalizeBoltPattern(s).includes('BLANK');
    // A vehicle looked up in this page's fitment list has `bolt_patterns`. One restored from
    // localStorage that this page's list doesn't contain (e.g. a truck picked on a 4Play page,
    // then viewed on a sports wheel, or a car picked elsewhere then viewed on a 4Play wheel)
    // carries the same data as `boltPatterns` — use it so fit is still answered by bolt pattern.
    const vehiclePatterns = (v) => Array.from(new Set((v.bolt_patterns || v.boltPatterns || []).map(normalizeBoltPattern)));

    // Each entry in the fitment data is already one distinct vehicle generation (e.g. "XE30
    // IS" vs "XE20 IS" are separate entries) — there's no further grouping to do here.
    const vehicleCoversYear = (v, year) => {
        if (v.year_start == null) return false;
        if (year < v.year_start) return false;
        if (v.year_end === 'current') return true;
        if (v.year_end == null) return year === v.year_start;
        return year <= v.year_end;
    };

    customElements.define('vehicle-fitment-selector', class extends Core {

        elements = {
            yearSelect: '[data-fitment-year]',
            makeSelect: '[data-fitment-make]',
            modelSelect: '[data-fitment-model]',
            checkButton: '[data-fitment-check]',
            formEl: '[data-fitment-form]',
            bannerEl: '[data-fitment-banner]',
            bannerStatus: '[data-fitment-banner-status]',
            disableToggle: '[data-fitment-disable-toggle]',
            summaryText: '[data-fitment-summary-text]',
            changeButton: '[data-fitment-change]',
            resultEl: '[data-fitment-result]',
            confirmedBuildsJSON: '[data-confirmed-builds-json]',
            productBoltPatternsJSON: '[data-product-bolt-patterns-json]',
            productTagsJSON: '[data-product-tags-json]',
            productVendorJSON: '[data-product-vendor-json]',
            productTypeJSON: '[data-product-type-json]',
            productWheelDiametersJSON: '[data-product-wheel-diameters-json]',
        }

        async render() {
            this.vehicles = [];
            this.confirmedBuilds = this._readJSONScript(this.$confirmedBuildsJSON);
            this.productBoltPatterns = (this._readJSONScript(this.$productBoltPatternsJSON) || [])
                .map(normalizeBoltPattern);
            this.productVendor = this._readJSONScript(this.$productVendorJSON) || '';
            this.productType = this._readJSONScript(this.$productTypeJSON) || '';
            this.productTags = this._readJSONScript(this.$productTagsJSON) || [];
            this.productWheelDiameterValues = this._readJSONScript(this.$productWheelDiametersJSON) || [];

            try {
                const res = await fetch(this.getAttribute('data-fitment-src'));
                this.vehicles = await res.json();
            } catch (e) {
                console.error('Vehicle fitment data failed to load', e);
                return;
            }

            this._buildYearIndex();
            this._populateYears();
            this._bindEvents();

            const stored = this._getStoredVehicle();
            if (stored && stored.year && stored.make && stored.model) {
                this._showSummary(stored, { publish: false });
            } else {
                this._showForm();
            }
        }

        _readJSONScript(el) {
            if (!el) return null;
            try {
                return JSON.parse(el.textContent);
            } catch (e) {
                return null;
            }
        }

        _bindEvents() {
            this.$yearSelect.addEventListener('change', () => this._onYearChange());
            this.$makeSelect.addEventListener('change', () => this._onMakeChange());
            this.$modelSelect.addEventListener('change', () => this._onModelChange());
            this.$formEl.addEventListener('submit', (e) => {
                e.preventDefault();
                this._applySelection();
            });
            this.$changeButton?.addEventListener('click', (e) => {
                e.preventDefault();
                this._clearVehicle();
            });
            this.$disableToggle?.addEventListener('change', (e) => {
                this._setFilterDisabled(e.target.checked);
                const stored = this._getStoredVehicle();
                if (stored) this._publishVehicleChange(stored);
            });
            // Re-render the result text when the customer picks a different Bolt Pattern (option 0)
            // so a "Blank (Custom)" selection shows the custom-drilled message.
            if (BLANK_SELECTED_VENDORS.includes(this.productVendor)) {
                document.addEventListener('change', (e) => {
                    const t = e.target;
                    if (!t || !t.hasAttribute || !t.hasAttribute('data-option-value') || t.getAttribute('data-option-index') !== '0') return;
                    setTimeout(() => {
                        const stored = this._getStoredVehicle();
                        if (stored && stored.year && stored.make && stored.model && !this.$bannerEl.hidden) this._showSummary(stored, { publish: false });
                    }, 0);
                });
            }
        }

        _buildYearIndex() {
            const maxYear = new Date().getFullYear() + 1;
            const years = new Set();
            this.vehicles.forEach(v => {
                if (v.year_start == null) return;
                const end = v.year_end === 'current' ? maxYear : (v.year_end ?? v.year_start);
                for (let y = v.year_start; y <= end; y++) years.add(y);
            });
            this.years = Array.from(years).sort((a, b) => b - a);
        }

        _populateYears() {
            this.$yearSelect.innerHTML = '<option value="">Year</option>' +
                this.years.map(y => `<option value="${y}">${y}</option>`).join('');
        }

        _onYearChange() {
            const year = parseInt(this.$yearSelect.value, 10);
            this.$modelSelect.innerHTML = '<option value="">Model</option>';
            this.$modelSelect.disabled = true;
            this.$checkButton.disabled = true;

            if (!year) {
                this.$makeSelect.innerHTML = '<option value="">Make</option>';
                this.$makeSelect.disabled = true;
                return;
            }

            const makes = Array.from(new Set(
                this.vehicles
                    .filter(v => vehicleCoversYear(v, year))
                    .map(v => v.make)
            )).sort();

            this.$makeSelect.innerHTML = '<option value="">Make</option>' +
                makes.map(m => `<option value="${this._esc(m)}">${this._esc(m)}</option>`).join('');
            this.$makeSelect.disabled = false;
        }

        _onMakeChange() {
            const year = parseInt(this.$yearSelect.value, 10);
            const make = this.$makeSelect.value;
            this.$checkButton.disabled = true;

            if (!make) {
                this.$modelSelect.innerHTML = '<option value="">Model</option>';
                this.$modelSelect.disabled = true;
                return;
            }

            const models = this.vehicles
                .filter(v => v.make === make && vehicleCoversYear(v, year))
                .map(v => v.model)
                .sort();

            this.$modelSelect.innerHTML = '<option value="">Model</option>' +
                models.map(m => `<option value="${this._esc(m)}">${this._esc(m)}</option>`).join('');
            this.$modelSelect.disabled = false;
        }

        _onModelChange() {
            this.$checkButton.disabled = !this.$modelSelect.value;
        }

        _esc(str) {
            const div = document.createElement('div');
            div.textContent = str;
            return div.innerHTML;
        }

        _findVehicle(make, model, year) {
            return this.vehicles.find(v => v.make === make && v.model === model && vehicleCoversYear(v, year));
        }

        _applySelection() {
            const year = parseInt(this.$yearSelect.value, 10);
            const make = this.$makeSelect.value;
            const model = this.$modelSelect.value;
            if (!year || !make || !model) return;

            const vehicle = this._findVehicle(make, model, year);
            if (!vehicle) return;

            const boltPatterns = Array.from(new Set(vehicle.bolt_patterns.map(normalizeBoltPattern)));

            const selected = {
                year, make, model, boltPatterns,
                restrictedCollectionHandle: vehicle.restricted_collection_handle || null,
                wheelDiameterMin: vehicle.wheel_diameter_min ?? null,
                wheelDiameterMax: vehicle.wheel_diameter_max ?? null,
                restrictedVendors: vehicle.restricted_vendors || null,
                restrictedStrict: vehicle.restricted_strict || false,
                // Set when the pick came from the 4Play-only list (see 4play-fitment.json), so
                // collection-level filters built for the car database can leave it alone.
                fitmentSource: vehicle.source || null,
                // Blank-only vehicles pre-check just "Blank (Custom)" in the collection's Bolt
                // Pattern filter (see vehicle-filter-autocheck.js) — boltPatterns stays the real one.
                blankOnly: vehicle.blank_only || false,
            };
            this._storeVehicle(selected);
            this._showSummary(selected);
        }

        _clearVehicle() {
            localStorage.removeItem(STORAGE_KEY);
            localStorage.removeItem(DISABLE_FILTER_KEY);
            this._showForm();
            this._publishVehicleChange({ year: null, make: null, model: null, boltPatterns: [] });
        }

        _isFilterDisabled() {
            return localStorage.getItem(DISABLE_FILTER_KEY) === '1';
        }

        _setFilterDisabled(disabled) {
            if (disabled) {
                localStorage.setItem(DISABLE_FILTER_KEY, '1');
            } else {
                localStorage.removeItem(DISABLE_FILTER_KEY);
            }
        }

        _publishVehicleChange(vehicle) {
            const effective = this._isFilterDisabled()
                ? { ...vehicle, boltPatterns: [], wheelDiameterMin: null, wheelDiameterMax: null }
                : vehicle;
            publishEvent('vehicle:change', effective, this.sectionId);
        }

        _storeVehicle(v) {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(v));
        }

        _getStoredVehicle() {
            try {
                const raw = localStorage.getItem(STORAGE_KEY);
                return raw ? JSON.parse(raw) : null;
            } catch (e) {
                return null;
            }
        }

        _showForm() {
            this.$formEl.hidden = false;
            this.$bannerEl.hidden = true;
        }

        // A curated vehicle names compatible products either by tag (restricted_tags — specific
        // models, like the C6 Corvette's Rohana/Stance list) or by whole vendor (restricted_vendors
        // — e.g. the C7 Corvette's "everything from AL13/Blaque Diamond/MRR/Ferrada" inclusion),
        // since tagging every product of a whole brand isn't practical. By default either one is
        // enough (OR). restricted_match_mode: 'and' requires BOTH — e.g. the C8 Stingray/Z51 needs
        // a wheel to be one of its 5 confirmed staggered sizes (tag) AND from one of its approved
        // brands (vendor); a same-size wheel from an unlisted brand shouldn't read as confirmed.
        _curatedMatch(full) {
            if (!full.restricted_tags?.length && !full.restricted_vendors?.length) return false;
            const tagMatch = full.restricted_tags?.length && this.productTags.some(t => full.restricted_tags.includes(t));
            const vendorMatch = full.restricted_vendors?.length && full.restricted_vendors.includes(this.productVendor);
            if (full.restricted_match_mode === 'and') {
                return Boolean(tagMatch && vendorMatch);
            }
            return Boolean(tagMatch || vendorMatch);
        }

        // A product's available diameters. Reads the real "Wheel Diameter" variant option
        // values (e.g. "19 Inch") first — the same source product-variant-selector.js uses for
        // its own fading, and guaranteed in sync with what's actually orderable. Falls back to
        // "Wheel Size: N" tags only if a product has no Wheel Diameter option at all, since tags
        // are manually applied and can drift out of sync with the real variants (e.g. a product
        // whose 19" variants were added after its tags were originally set, still only tagged
        // "Wheel Size: 18" — this caused a real false "does not fit" for a 19"-20" vehicle on a
        // wheel that's genuinely offered in 19").
        _productDiameters() {
            if (this.productWheelDiameterValues.length) {
                return this.productWheelDiameterValues
                    .map(v => {
                        const m = /(\d+(?:\.\d+)?)/.exec(String(v));
                        return m ? parseFloat(m[1]) : null;
                    })
                    .filter(d => d != null);
            }
            return this.productTags
                .map(t => /^wheel size:\s*(\d+(?:\.\d+)?)$/i.exec(t.trim()))
                .filter(Boolean)
                .map(m => parseFloat(m[1]));
        }

        // null = no size data on one side or the other (don't block); true/false = a real,
        // confirmed answer. A "Blank (Custom)" bolt pattern otherwise matches ANY vehicle
        // needing that pattern regardless of whether the wheel is even offered in a size that
        // clears the car — e.g. a 22"-only wheel showing as a fit for a car topping out at 20".
        _fitsSizeRange(full) {
            if (full.wheel_diameter_min == null || full.wheel_diameter_max == null) return null;
            const diameters = this._productDiameters();
            if (!diameters.length) return null;
            return diameters.some(d => d >= full.wheel_diameter_min && d <= full.wheel_diameter_max);
        }

        _fitsThisProduct(vehicle) {
            // Some vehicles (e.g. the C8 Z06's 20x10F/21x13R) can't be answered by bolt pattern
            // alone — restricted_tags/restricted_vendors names the exact products confirmed to
            // fit, so for those vehicles fit is determined by that membership instead.
            // restricted_strict controls what happens on a NON-match: strict vehicles (only one
            // specific model works, like the Mustang GT350/GT500's Velgen VF5) hard-deny anything
            // else; non-strict vehicles (C6/C7 Corvette, C8 Stingray — a curated list of
            // *known-good* products, not an exhaustive one) fall back to the normal bolt-pattern
            // check instead of a false deny.
            const full = this._findVehicle(vehicle.make, vehicle.model, vehicle.year) || vehicle;
            if (full.restricted_tags?.length || full.restricted_vendors?.length) {
                if (this._curatedMatch(full)) return true;
                if (full.restricted_match_mode === 'and') {
                    // Brand is a hard gate, confirmed size is a bonus on top of it: an unlisted
                    // brand is denied outright, but a listed brand in a size outside the
                    // confirmed list just loses the "confirmed" messaging and falls through to
                    // the normal bolt-pattern/size check below instead of being denied too.
                    const vendorMatch = full.restricted_vendors.includes(this.productVendor);
                    if (!vendorMatch) return false;
                } else if (full.restricted_strict) {
                    return false;
                }
            }
            const boltPatterns = vehiclePatterns(full);
            if (!this.productBoltPatterns.length || !boltPatterns.length) return null;

            const boltFits = this.productBoltPatterns.some(isBlankBoltPattern)
                || boltPatterns.some(bp => this.productBoltPatterns.includes(bp));
            if (!boltFits) return false;

            return this._fitsSizeRange(full) !== false;
        }

        _showSummary(vehicle, { publish = true } = {}) {
            this.$formEl.hidden = true;
            this.$bannerEl.hidden = false;
            this.$summaryText.textContent = `${vehicle.year} ${vehicle.make} ${vehicle.model}`;

            // A curated (restricted_tags/restricted_vendors) vehicle may have been stored with
            // boltPatterns: [] by an older homepage-widget path. Re-deriving the real patterns
            // (and diameter range) here means the "matching bolt patterns below are highlighted"
            // fade-out (driven by the vehicle:change event, in product-variant-selector.js)
            // still works regardless of how the vehicle was originally selected.
            const full = this._findVehicle(vehicle.make, vehicle.model, vehicle.year) || vehicle;
            const realBoltPatterns = vehiclePatterns(full);
            const effectiveVehicle = {
                ...vehicle,
                boltPatterns: realBoltPatterns,
                wheelDiameterMin: full.wheel_diameter_min ?? null,
                wheelDiameterMax: full.wheel_diameter_max ?? null,
            };

            const fits = this._fitsThisProduct(effectiveVehicle);
            this.$bannerEl.classList.remove('vehicle-fitment-selector-banner--fits', 'vehicle-fitment-selector-banner--no-fit', 'vehicle-fitment-selector-banner--unverified');
            if (fits === true && this._isUnverifiedFitment(effectiveVehicle)) {
                this.$bannerEl.classList.add('vehicle-fitment-selector-banner--unverified');
                this.$bannerStatus.textContent = 'Unverified fitment';
            } else if (fits === true) {
                this.$bannerEl.classList.add('vehicle-fitment-selector-banner--fits');
                this.$bannerStatus.textContent = '✓ It fits your vehicle';
            } else if (fits === false) {
                this.$bannerEl.classList.add('vehicle-fitment-selector-banner--no-fit');
                this.$bannerStatus.textContent = '✕ Does not fit your vehicle';
            } else {
                this.$bannerStatus.textContent = '';
            }

            if (this.$disableToggle) this.$disableToggle.checked = this._isFilterDisabled();

            this._renderFitmentResult(effectiveVehicle, fits);
            if (publish) this._publishVehicleChange(effectiveVehicle);
        }

        // Off-road wheels: bolt pattern can match, but offset/width/backspacing vary a lot on
        // aftermarket off-road wheels, so fitment is only treated as verified when it comes from
        // the vendor's own fitment sheet (4Play wheel + a vehicle picked from the 4Play list).
        _isUnverifiedFitment(vehicle) {
            if (this.productType !== 'Offroad Wheels') return false;
            return !(this.productVendor === '4Play Wheels' && vehicle && vehicle.fitmentSource === '4play');
        }

        // True while the Blank (Custom) bolt pattern is the one selected in the product's variant
        // picker (product-variant-selector keeps the live selection in currentOptions).
        _selectedBoltIsBlank() {
            const picker = document.querySelector('product-variant-selector');
            const bolt = picker && picker.currentOptions && picker.currentOptions[0];
            return Boolean(bolt) && isBlankBoltPattern(bolt);
        }

        _renderFitmentResult(vehicle, fits) {
            if (!this.$resultEl) return;

            const parts = [];
            const full = this._findVehicle(vehicle.make, vehicle.model, vehicle.year) || vehicle;
            const isCurated = full.restricted_tags?.length || full.restricted_vendors?.length;
            const curatedMatch = isCurated && this._curatedMatch(full);
            const boltPatterns = vehiclePatterns(full);
            const boltText = this._esc(boltPatterns.join(' / '));
            const hasRealMatch = boltPatterns.some(bp =>
                this.productBoltPatterns.includes(bp) && !isBlankBoltPattern(bp)
            );
            const hasBlankOption = this.productBoltPatterns.some(isBlankBoltPattern);
            const sizeFits = this._fitsSizeRange(full);
            const blankLeadTime = BLANK_LEAD_TIME_BY_VENDOR[this.productVendor] || DEFAULT_BLANK_LEAD_TIME;
            const blankSelected = BLANK_SELECTED_VENDORS.includes(this.productVendor) && hasBlankOption && this._selectedBoltIsBlank();
            const blankSelectedMsg = `<p class="vehicle-fitment-result-ok">Blank (Custom) is custom drilled to order — bolt pattern, offset, and center bore will be machined to your vehicle's ${boltText} spec for a flush fitment. Lead time: ${blankLeadTime}</p>`;
            // In 'and' mode, brand is the hard gate and confirmed size is a bonus on top of it —
            // only an unlisted brand triggers the strict-deny message; a listed brand in an
            // unconfirmed size falls through to the normal bolt-pattern messaging below instead.
            const strictDeny = full.restricted_match_mode === 'and'
                ? Boolean(full.restricted_vendors?.length && !full.restricted_vendors.includes(this.productVendor))
                : Boolean(full.restricted_strict);
            const sizeRangeText = (full.wheel_diameter_min != null && full.wheel_diameter_max != null)
                ? (full.wheel_diameter_min === full.wheel_diameter_max
                    ? `${full.wheel_diameter_min}"`
                    : `${full.wheel_diameter_min}"-${full.wheel_diameter_max}"`)
                : '';

            if (isCurated && curatedMatch) {
                parts.push(`<p class="vehicle-fitment-result-ok">This is one of the specific wheels confirmed to fit your ${this._esc(vehicle.model)}.</p>`);
                if (sizeFits === false) {
                    parts.push(`<p class="vehicle-fitment-result-ok">Note: this wheel isn't offered in a size that fits your vehicle (needs ${sizeRangeText}) — matching sizes below are highlighted</p>`);
                } else if (boltPatterns.length && blankSelected) {
                    parts.push(blankSelectedMsg);
                } else if (boltPatterns.length && hasRealMatch) {
                    parts.push(`<p class="vehicle-fitment-result-ok">Bolt pattern: ${boltText} — matching bolt patterns below are highlighted</p>`);
                } else if (hasBlankOption) {
                    parts.push(`<p class="vehicle-fitment-result-ok">This wheel is custom drilled to order — bolt pattern, offset, and center bore will be machined to your vehicle's ${boltText} spec for a flush fitment. Lead time: ${blankLeadTime}</p>`);
                }
            } else if (isCurated && strictDeny) {
                const handle = full.restricted_collection_handle;
                const link = handle ? ` — <a href="/collections/${this._esc(handle)}">see what fits your vehicle</a>` : '';
                parts.push(`<p class="vehicle-fitment-result-ok">Correct wheel specs not available for your ${this._esc(vehicle.model)}${link}</p>`);
            } else {
                if (boltPatterns.length) {
                    if (sizeFits === false && (hasRealMatch || hasBlankOption)) {
                        parts.push(`<p class="vehicle-fitment-result-ok">This wheel isn't offered in a size that fits your vehicle (needs ${sizeRangeText}) — matching sizes below are highlighted</p>`);
                    } else if (blankSelected) {
                        parts.push(blankSelectedMsg);
                    } else if (hasRealMatch) {
                        parts.push(`<p class="vehicle-fitment-result-ok">Bolt pattern: ${boltText} — matching bolt patterns below are highlighted</p>`);
                    } else if (hasBlankOption) {
                        parts.push(`<p class="vehicle-fitment-result-ok">This wheel is custom drilled to order — bolt pattern, offset, and center bore will be machined to your vehicle's ${boltText} spec for a flush fitment. Lead time: ${blankLeadTime}</p>`);
                    } else {
                        parts.push(`<p class="vehicle-fitment-result-ok">Your vehicle needs bolt pattern ${boltText} — this product doesn't offer that pattern</p>`);
                    }
                }
            }

            const vehicleModel = vehicle.model.toLowerCase();
            const match = (this.confirmedBuilds || []).find(b => {
                const buildMake = (b.make || '').trim().toLowerCase();
                const buildModel = (b.model || '').trim().toLowerCase();
                if (buildMake !== vehicle.make.toLowerCase()) return false;
                return buildModel.includes(vehicleModel) || vehicleModel.includes(buildModel);
            });

            if (match) {
                parts.push(`<p class="vehicle-fitment-result-confirmed">✓ Confirmed fitment: a customer's ${this._esc(match.year || vehicle.year)} ${this._esc(match.make)} ${this._esc(match.model)} is running ${this._esc(match.wheel_size || 'this wheel')}${match.url ? ` — <a href="${match.url}">see the build</a>` : ''}</p>`);
            }

            if (fits === true && this._isUnverifiedFitment(vehicle)) {
                parts.unshift('<p class="vehicle-fitment-result-ok">Our team confirms offset and fitment before shipping. Questions? Call <a href="tel:8773774229">(877) 377-4229</a>.</p>');
            }

            this.$resultEl.innerHTML = parts.join('');
        }
    });
})();
